import React, { useEffect, useState } from 'react';
import { Star, MessageSquarePlus, Sparkles, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';

const moodOptions = [
  { label: 'Love it', value: 'love', description: 'Pure delight' },
  { label: 'Like it', value: 'like', description: 'Feels good' },
  { label: 'It’s ok', value: 'ok', description: 'Neutral vibes' },
  { label: 'Confused', value: 'confused', description: 'Something felt off' },
  { label: 'Frustrated', value: 'frustrated', description: 'Needs urgent attention' },
];

const topicOptions = [
  { label: 'Product Experience', value: 'product' },
  { label: 'Bug Report', value: 'bug' },
  { label: 'Billing / Pricing', value: 'billing' },
  { label: 'New Feature', value: 'feature' },
  { label: 'Other', value: 'other' },
];

const Feedback = () => {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [mood, setMood] = useState('love');
  const [topic, setTopic] = useState('product');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: prev.email || user.email || '',
      }));
    }
  }, [user]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!rating) {
      toast.error('Please select a rating.');
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/feedbacks', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        rating,
        mood,
        topic,
        message: formData.message.trim(),
      });
      toast.success('Thanks! Your feedback fuels the roadmap.');
      setFormData({ name: user?.firstName || '', email: user?.email || '', message: '' });
      setRating(5);
      setMood('love');
      setTopic('product');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to send feedback right now.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 lg:py-20">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-purple-700 font-semibold shadow">
              <MessageSquarePlus className="h-4 w-4" />
              Feedback Studio
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-6 mb-4">Guide the next chapter</h1>
            <p className="text-lg text-gray-600">
              Tell us how the desktop PWA-inspired experience feels, what sparks joy, and what should ship next.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            <div className="bg-white rounded-3xl border border-purple-100 shadow-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Rate your experience</h2>
              <p className="text-gray-600 mb-6">
                From floating gradients to install prompts—what’s landing? Share details so we can tune the UI or roadmap.
              </p>
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">Overall rating</p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      type="button"
                      key={value}
                      onClick={() => setRating(value)}
                      className={`w-12 h-12 rounded-2xl border ${
                        rating >= value ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white border-transparent' : 'border-purple-100 text-purple-500 bg-white'
                      } flex items-center justify-center`}
                    >
                      <Star className="h-5 w-5" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">How are you feeling?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {moodOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setMood(option.value)}
                      className={`text-left rounded-2xl border px-4 py-3 hover:shadow ${
                        mood === option.value
                          ? 'bg-purple-600 text-white border-transparent'
                          : 'border-purple-100 text-purple-700 bg-white'
                      }`}
                    >
                      <p className="font-semibold">{option.label}</p>
                      <p className="text-xs opacity-80">{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-purple-100 px-4 py-3 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                    placeholder="Avery Bennett"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-purple-100 px-4 py-3 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                    placeholder="you@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Feedback Topic
                  </label>
                  <select
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="w-full rounded-xl border border-purple-100 px-4 py-3 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                  >
                    {topicOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Feedback
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-purple-100 px-4 py-3 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                    placeholder="Tell us what to double down on or what feels off..."
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {submitting ? 'Sending...' : 'Share feedback'}
                </button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-3xl p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-6 w-6" />
                  <h3 className="text-xl font-semibold">What happens next?</h3>
                </div>
                <p className="text-white/90">
                  Every submission enters our roadmap triage. If we need more context, a product designer from the PWA
                  strike team will reach out.
                </p>
              </div>
              <div className="rounded-3xl border border-purple-100 bg-white p-6 shadow-xl">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Top requests this month</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Animated avatars synced across devices</li>
                  <li>• Story playlists for classroom circle time</li>
                  <li>• One-click privacy reports for schools</li>
                </ul>
              </div>
              <div className="rounded-3xl border border-purple-100 bg-white p-6 shadow-xl">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Need direct support?</h4>
                <p className="text-gray-600 mb-4">
                  Visit the Help Center for guides or ping Story Concierge. We respond faster than most bedtime routines.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href="/help" className="px-5 py-2 rounded-xl bg-purple-50 text-purple-700 font-semibold">
                    Help Center
                  </a>
                  <a href="/contact" className="px-5 py-2 rounded-xl border border-purple-100 text-purple-700 font-semibold bg-white">
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Feedback;


